/**
 * SalesDashboard.tsx — "Performance de Vendas Padrão Ouro"
 *
 * Architecture (Clean Code):
 *   - Logic lives here (state, fetching, error handling)
 *   - Each child component is a pure "View" that receives data via props
 *   - State shape follows PerformanceState (future-proof for externalMetrics)
 *
 * Security:
 *   - Zero Supabase credentials in frontend
 *   - All data fetched via /api/* (FastAPI backend with Service Role Key)
 *   - No eval, no dangerouslySetInnerHTML, no dynamic SQL
 */
import { useState, useEffect, useCallback } from 'react';
import toast from 'react-hot-toast';
import {
  SalesService,
  PerformanceState,
  VendaManualInput,
  FunnelMetrics,
  formatBRL,
  today,
  firstDayOfMonth,
  currentYearMonth,
} from '../services/salesService';
import Vendometro from './sales/Vendometro';
import LeadsLineChart from './sales/LeadsLineChart';
import ForecastCard from './sales/ForecastCard';

// ─── Loading helpers ──────────────────────────────────────────────────────
const StatCard = ({
  label, value, sub, icon, color = 'text-gray-800 dark:text-white',
}: {
  label: string; value: string; sub?: string; icon: string; color?: string;
}) => (
  <div className="bg-white dark:bg-gray-800 rounded-xl shadow p-4 flex items-start gap-3">
    <span className="text-2xl">{icon}</span>
    <div>
      <p className="text-xs text-gray-500 dark:text-gray-400 uppercase tracking-wide">{label}</p>
      <p className={`text-xl font-bold ${color}`}>{value}</p>
      {sub && <p className="text-xs text-gray-400 dark:text-gray-500 mt-0.5">{sub}</p>}
    </div>
  </div>
);

// ─── Main Component (Logic Layer) ─────────────────────────────────────────
export default function SalesDashboard() {
  // ── State — shaped for future externalMetrics injection ─────────────────
  const [state, setState] = useState<PerformanceState>({
    salesSummary: null,
    meta:         null,
    leadsPerDay:  [],
    dateRange:    { start: firstDayOfMonth(), end: today() },
    yearMonth:    currentYearMonth(),
    externalMetrics: undefined,  // ready for future injection (Instagram, TikTok…)
  });

  const [loadingLeads,   setLoadingLeads]   = useState(true);
  const [loadingSales,   setLoadingSales]   = useState(true);
  const [loadingMeta,    setLoadingMeta]    = useState(true);
  const [loadingFunnel,  setLoadingFunnel]  = useState(true);
  const [updatingMeta,   setUpdatingMeta]   = useState(false);
  const [errorLeads,     setErrorLeads]     = useState<string | null>(null);
  const [errorSales,     setErrorSales]     = useState<string | null>(null);
  const [funnelMetrics,  setFunnelMetrics]  = useState<FunnelMetrics | null>(null);

  // ── Modal: nova venda manual ───────────────────────────────────────
  const [showModal,    setShowModal]    = useState(false);
  const [savingVenda,  setSavingVenda]  = useState(false);
  const [form, setForm] = useState<VendaManualInput>({
    produto:     '',
    valor_bruto: 0,
    status:      'pago',
    data_hora:   today(),
    cliente:     '',
  });

  // ── Fetch leads per day ───────────────────────────────────────────────────
  const fetchLeads = useCallback(async () => {
    setLoadingLeads(true);
    setErrorLeads(null);
    try {
      const res = await SalesService.getLeadsPerDay(
        state.dateRange.start,
        state.dateRange.end
      );
      setState((prev) => ({ ...prev, leadsPerDay: res.data }));
    } catch (err: any) {
      const msg = err?.response?.data?.detail || 'Erro ao carregar leads';
      setErrorLeads(msg);
    } finally {
      setLoadingLeads(false);
    }
  }, [state.dateRange.start, state.dateRange.end]);

  // ── Fetch sales summary ───────────────────────────────────────────────────
  const fetchSales = useCallback(async () => {
    setLoadingSales(true);
    setErrorSales(null);
    try {
      const res = await SalesService.getSalesSummary(state.yearMonth);
      setState((prev) => ({ ...prev, salesSummary: res }));
    } catch (err: any) {
      // If vendas table doesn't exist yet, show a graceful empty state
      const status = err?.response?.status;
      if (status === 500 || status === 503) {
        setState((prev) => ({
          ...prev,
          salesSummary: {
            year_month: state.yearMonth,
            total_vendas: 0,
            vendas_count: 0,
            pending_count: 0,
            cancelled_count: 0,
            by_product: {},
          },
        }));
        setErrorSales(null);  // Treat as empty, not error
      } else {
        setErrorSales(err?.response?.data?.detail || 'Erro ao carregar vendas');
      }
    } finally {
      setLoadingSales(false);
    }
  }, [state.yearMonth]);

  // ── Fetch meta ────────────────────────────────────────────────────────────
  const fetchMeta = useCallback(async () => {
    setLoadingMeta(true);
    try {
      const meta = await SalesService.getMeta();
      setState((prev) => ({ ...prev, meta }));
    } catch {
      setState((prev) => ({ ...prev, meta: { meta_mensal: 0, updated_at: null } }));
    } finally {
      setLoadingMeta(false);
    }
  }, []);

  // ── Fetch funil de vendas ─────────────────────────────────────────────────
  const fetchFunnel = useCallback(async () => {
    setLoadingFunnel(true);
    try {
      const data = await SalesService.getFunnelMetrics(
        state.dateRange.start,
        state.dateRange.end,
      );
      setFunnelMetrics(data);
    } catch {
      setFunnelMetrics(null);
    } finally {
      setLoadingFunnel(false);
    }
  }, [state.dateRange.start, state.dateRange.end]);

  // ── Initial load ──────────────────────────────────────────────────────────
  useEffect(() => { fetchLeads();  }, [fetchLeads]);
  useEffect(() => { fetchSales();  }, [fetchSales]);
  useEffect(() => { fetchMeta();   }, [fetchMeta]);
  useEffect(() => { fetchFunnel(); }, [fetchFunnel]);

  // ── Update meta ───────────────────────────────────────────────────────────
  const handleMetaChange = async (newMeta: number) => {
    setUpdatingMeta(true);
    try {
      const updated = await SalesService.setMeta(newMeta);
      setState((prev) => ({ ...prev, meta: updated }));
      toast.success(`Meta atualizada para ${formatBRL(newMeta)}`);
    } catch {
      toast.error('Erro ao salvar meta');
    } finally {
      setUpdatingMeta(false);
    }
  };
  // ── Salvar venda manual ─────────────────────────────────────────────
  const handleAddVenda = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!form.produto.trim())   { toast.error('Informe o produto'); return; }
    if (form.valor_bruto <= 0)  { toast.error('Informe um valor válido'); return; }
    setSavingVenda(true);
    try {
      await SalesService.addVendaManual({
        ...form,
        cliente: form.cliente || undefined,
      });
      toast.success('✅ Venda registrada!');
      setShowModal(false);
      setForm({ produto: '', valor_bruto: 0, status: 'pago',
                data_hora: today(), cliente: '' });
      fetchSales();
    } catch (err: any) {
      toast.error(err?.response?.data?.detail || 'Erro ao salvar venda');
    } finally {
      setSavingVenda(false);
    }
  };
  // ── Date range handlers ───────────────────────────────────────────────────
  const handleStartDate = (d: string) =>
    setState((prev) => ({ ...prev, dateRange: { ...prev.dateRange, start: d } }));
  const handleEndDate = (d: string) =>
    setState((prev) => ({ ...prev, dateRange: { ...prev.dateRange, end: d } }));

  const handleYearMonthChange = (ym: string) =>
    setState((prev) => ({ ...prev, yearMonth: ym }));

  // ── Derived values ────────────────────────────────────────────────────────
  const totalVendas  = state.salesSummary?.total_vendas ?? 0;
  const metaMensal   = state.meta?.meta_mensal ?? 0;
  const totalLeads   = state.leadsPerDay.reduce((s, d) => s + d.count, 0);

  const isLoading = loadingLeads && loadingSales && loadingMeta;

  // ── Render ────────────────────────────────────────────────────────────────
  return (
    <div className="space-y-6">
      {/* ── Page header ── */}
      <div className="flex flex-col sm:flex-row sm:items-end justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 dark:text-white">
            ⭐ Performance de Vendas
          </h1>
          <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">
            Análise em tempo real · dados diretamente do Supabase
          </p>
        </div>

        {/* Month selector + actions */}
        <div className="flex flex-wrap items-center gap-2 text-sm">
          <label className="text-gray-500 dark:text-gray-400">Mês:</label>
          <input
            type="month"
            value={state.yearMonth}
            onChange={(e) => handleYearMonthChange(e.target.value)}
            className="border border-gray-300 dark:border-gray-600 rounded px-2 py-1 dark:bg-gray-800 dark:text-white"
          />
          <button
            onClick={() => { fetchLeads(); fetchSales(); fetchFunnel(); }}
            className="px-3 py-1 bg-primary-600 text-white rounded text-xs hover:bg-primary-700 transition-colors"
          >
            🔄 Atualizar
          </button>
          <button
            onClick={() => setShowModal(true)}
            className="px-3 py-1 bg-green-600 text-white rounded text-xs hover:bg-green-700 transition-colors font-medium"
          >
            + Nova Venda
          </button>
        </div>
      </div>

      {/* ── Top stat cards ── */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
        <StatCard
          icon="👥"
          label="Leads no período"
          value={loadingLeads ? '…' : String(totalLeads)}
          sub={`${state.dateRange.start} → ${state.dateRange.end}`}
        />
        <StatCard
          icon="💰"
          label="Total vendido"
          value={loadingSales ? '…' : formatBRL(totalVendas)}
          sub={`${state.salesSummary?.vendas_count ?? 0} vendas pagas`}
          color="text-green-600"
        />
        <StatCard
          icon="⏳"
          label="Pendentes"
          value={loadingSales ? '…' : String(state.salesSummary?.pending_count ?? 0)}
          color="text-yellow-600"
        />
        <StatCard
          icon="🎯"
          label="Meta mensal"
          value={loadingMeta ? '…' : formatBRL(metaMensal)}
          sub={metaMensal > 0 ? `${((totalVendas / metaMensal) * 100).toFixed(1)}% atingida` : 'Clique em Vendômetro para definir'}
        />
      </div>

      {/* ── Leads line chart ── */}
      <LeadsLineChart
        data={state.leadsPerDay}
        loading={loadingLeads}
        error={errorLeads}
        startDate={state.dateRange.start}
        endDate={state.dateRange.end}
        onStartDateChange={handleStartDate}
        onEndDateChange={handleEndDate}
      />

      {/* ── Vendômetro + Forecast side by side ── */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <Vendometro
          metaMensal={metaMensal}
          totalVendas={totalVendas}
          onMetaChange={handleMetaChange}
          isUpdating={updatingMeta}
        />

        <ForecastCard
          totalVendas={totalVendas}
          metaMensal={metaMensal}
          yearMonth={state.yearMonth}
          loading={loadingSales || loadingMeta}
        />
      </div>

      {/* ── Products breakdown ── */}
      {!loadingSales && !errorSales && state.salesSummary && Object.keys(state.salesSummary.by_product).length > 0 && (
        <div className="bg-white dark:bg-gray-800 rounded-xl shadow p-5">
          <h2 className="text-base font-semibold text-gray-800 dark:text-white mb-4">
            📦 Breakdown por Produto
          </h2>
          <div className="space-y-3">
            {Object.entries(state.salesSummary.by_product).map(([produto, info]) => {
              const pct = totalVendas > 0 ? (info.total / totalVendas) * 100 : 0;
              return (
                <div key={produto}>
                  <div className="flex justify-between text-sm mb-1">
                    <span className="text-gray-700 dark:text-gray-300 font-medium">{produto}</span>
                    <span className="text-gray-500 dark:text-gray-400">
                      {info.count}× · {formatBRL(info.total)} ({pct.toFixed(1)}%)
                    </span>
                  </div>
                  <div className="w-full bg-gray-100 dark:bg-gray-700 rounded-full h-2">
                    <div
                      className="h-2 rounded-full bg-primary-500 transition-all duration-700"
                      style={{ width: `${pct}%` }}
                    />
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* ── Funil de Vendas ── */}
      <div className="bg-white dark:bg-gray-800 rounded-xl shadow p-5">
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-base font-semibold text-gray-900 dark:text-white">
            🏁 Funil de Vendas
          </h2>
          <span className="text-xs text-gray-400 dark:text-gray-500">
            {state.dateRange.start} → {state.dateRange.end}
          </span>
        </div>

        {loadingFunnel ? (
          <p className="text-sm text-gray-400 dark:text-gray-500 text-center py-4">Carregando funil…</p>
        ) : funnelMetrics ? (
          <>
            {/* Barras de funil */}
            {([
              { label: 'Fase 1 — Diagnóstico',  icon: '🩺', value: funnelMetrics.fase1_diagnostico, color: 'bg-blue-500',   conv: null },
              { label: 'Fase 2 — Script áudio', icon: '🎙️', value: funnelMetrics.fase2_audio,       color: 'bg-yellow-500', conv: funnelMetrics.conv_1_to_2 },
              { label: 'Fase 3 — Script venda', icon: '💬', value: funnelMetrics.fase3_venda,       color: 'bg-orange-500', conv: funnelMetrics.conv_2_to_3 },
              { label: 'Fase 4 — Compra',       icon: '🛒', value: funnelMetrics.fase4_compra,      color: 'bg-green-500',  conv: funnelMetrics.conv_3_to_4 },
            ] as { label: string; icon: string; value: number; color: string; conv: number | null }[]).map((phase) => {
              const pct = funnelMetrics.fase1_diagnostico > 0
                ? Math.round((phase.value / funnelMetrics.fase1_diagnostico) * 100)
                : 0;
              return (
                <div key={phase.label} className="mb-3">
                  <div className="flex items-center justify-between mb-1">
                    <span className="text-sm text-gray-700 dark:text-gray-300">
                      {phase.icon} {phase.label}
                    </span>
                    <div className="flex items-center gap-2">
                      {phase.conv !== null && (
                        <span className="text-xs text-gray-400 dark:text-gray-500">
                          ↓ {phase.conv}% conv.
                        </span>
                      )}
                      <span className="text-sm font-bold text-gray-800 dark:text-white">
                        {phase.value}
                      </span>
                    </div>
                  </div>
                  <div className="w-full bg-gray-100 dark:bg-gray-700 rounded-full h-2.5">
                    <div
                      className={`h-2.5 rounded-full ${phase.color} transition-all duration-700`}
                      style={{ width: `${Math.min(pct, 100)}%` }}
                    />
                  </div>
                </div>
              );
            })}

            {/* Conversão total */}
            <div className="mt-4 pt-3 border-t border-gray-100 dark:border-gray-700 flex items-center justify-between">
              <span className="text-xs text-gray-500 dark:text-gray-400">
                Conversão total (Diagnóstico → Compra)
              </span>
              <span className={`text-sm font-bold ${
                funnelMetrics.conv_total >= 5  ? 'text-green-600 dark:text-green-400' :
                funnelMetrics.conv_total >= 2  ? 'text-yellow-600 dark:text-yellow-400' :
                'text-red-500 dark:text-red-400'
              }`}>
                {funnelMetrics.conv_total}%
              </span>
            </div>
          </>
        ) : (
          <p className="text-sm text-gray-400 dark:text-gray-500 text-center py-4">
            Sem dados de funil para o período.
          </p>
        )}
      </div>

      {/* ── Error state for sales ── */}
      {errorSales && (
        <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-xl p-4 text-red-700 dark:text-red-400 text-sm">
          ❌ {errorSales}
          <button onClick={fetchSales} className="ml-3 underline text-xs">Tentar novamente</button>
        </div>
      )}

      {/* ── Security note (dev info) ── */}
      <p className="text-xs text-gray-400 dark:text-gray-600 text-center pb-2">
        🔒 Dados lidos via API segura · Supabase Service Key nunca exposta ao browser · RLS ativo
      </p>

      {/* ────────────────────────────────────────────────── */}
      {/* MODAL: Nova Venda Manual                                         */}
      {/* ────────────────────────────────────────────────── */}
      {showModal && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 px-4"
          onClick={(e) => { if (e.target === e.currentTarget) setShowModal(false); }}
        >
          <div className="bg-white dark:bg-gray-800 rounded-2xl shadow-2xl w-full max-w-md">
            {/* Header */}
            <div className="flex items-center justify-between px-6 pt-5 pb-3 border-b border-gray-100 dark:border-gray-700">
              <h2 className="text-lg font-bold text-gray-900 dark:text-white">💵 Nova Venda</h2>
              <button
                onClick={() => setShowModal(false)}
                className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-200 text-xl leading-none"
              >×</button>
            </div>

            {/* Form */}
            <form onSubmit={handleAddVenda} className="px-6 py-5 space-y-4">

              {/* Produto */}
              <div>
                <label className="block text-xs font-medium text-gray-600 dark:text-gray-400 mb-1">
                  Produto *
                </label>
                <input
                  type="text"
                  value={form.produto}
                  onChange={(e) => setForm((f) => ({ ...f, produto: e.target.value }))}
                  placeholder="Ex: Plano Mensal, Consultoria..."
                  required
                  className="w-full border border-gray-300 dark:border-gray-600 rounded-lg px-3 py-2 text-sm dark:bg-gray-700 dark:text-white focus:outline-none focus:ring-2 focus:ring-green-500"
                />
              </div>

              {/* Valor + Status (side by side) */}
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-xs font-medium text-gray-600 dark:text-gray-400 mb-1">
                    Valor (R$) *
                  </label>
                  <input
                    type="number"
                    min="0.01" step="0.01"
                    value={form.valor_bruto || ''}
                    onChange={(e) => setForm((f) => ({ ...f, valor_bruto: parseFloat(e.target.value) || 0 }))}
                    placeholder="0,00"
                    required
                    className="w-full border border-gray-300 dark:border-gray-600 rounded-lg px-3 py-2 text-sm dark:bg-gray-700 dark:text-white focus:outline-none focus:ring-2 focus:ring-green-500"
                  />
                </div>
                <div>
                  <label className="block text-xs font-medium text-gray-600 dark:text-gray-400 mb-1">
                    Status *
                  </label>
                  <select
                    value={form.status}
                    onChange={(e) => setForm((f) => ({ ...f, status: e.target.value as any }))}
                    className="w-full border border-gray-300 dark:border-gray-600 rounded-lg px-3 py-2 text-sm dark:bg-gray-700 dark:text-white focus:outline-none focus:ring-2 focus:ring-green-500"
                  >
                    <option value="pago">✅ Pago</option>
                    <option value="pendente">⏳ Pendente</option>
                    <option value="cancelado">❌ Cancelado</option>
                  </select>
                </div>
              </div>

              {/* Data da venda */}
              <div>
                <label className="block text-xs font-medium text-gray-600 dark:text-gray-400 mb-1">
                  Data da venda
                </label>
                <input
                  type="date"
                  value={(form.data_hora || today()).substring(0, 10)}
                  onChange={(e) => setForm((f) => ({ ...f, data_hora: e.target.value }))}
                  className="w-full border border-gray-300 dark:border-gray-600 rounded-lg px-3 py-2 text-sm dark:bg-gray-700 dark:text-white focus:outline-none focus:ring-2 focus:ring-green-500"
                />
              </div>

              {/* Cliente (opcional) */}
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-xs font-medium text-gray-600 dark:text-gray-400 mb-1">
                    Nome do cliente
                  </label>
                  <input
                    type="text"
                    value={form.cliente || ''}
                    onChange={(e) => setForm((f) => ({ ...f, cliente: e.target.value }))}
                    placeholder="Opcional"
                    className="w-full border border-gray-300 dark:border-gray-600 rounded-lg px-3 py-2 text-sm dark:bg-gray-700 dark:text-white focus:outline-none focus:ring-2 focus:ring-green-500"
                  />
                </div>
              </div>

              {/* Actions */}
              <div className="flex justify-end gap-3 pt-2">
                <button
                  type="button"
                  onClick={() => setShowModal(false)}
                  className="px-4 py-2 text-sm text-gray-600 dark:text-gray-400 hover:text-gray-800 dark:hover:text-gray-200 transition-colors"
                >
                  Cancelar
                </button>
                <button
                  type="submit"
                  disabled={savingVenda}
                  className="px-5 py-2 bg-green-600 text-white rounded-lg text-sm font-medium hover:bg-green-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                >
                  {savingVenda ? 'Salvando...' : '💾 Salvar Venda'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}
